// #226: URL params that must survive a filter/sort re-render, in the order they
// are appended. Deliberately NOT preserved: `page` (a filter apply resets to
// page 1, same as collections), `section_id`, `product_id` (new-plp.js scroll
// restore), `sort_by` and every `filter.*` — those are rebuilt from live control
// state in createSearchParams().
const PRESERVED_SEARCH_PARAMS = ['q', 'type', 'options[prefix]', 'options[unavailable_products]'];

// A11y (#96): mirror the view toggles' .active class onto aria-pressed so
// the buttons expose their state. Called after every site that mutates the
// class (drawer + column-toggle init, user toggle, resize regroup).
function syncViewBtnPressed() {
  document.querySelectorAll('.view-btn').forEach((btn) => {
    btn.setAttribute('aria-pressed', String(btn.classList.contains('active')));
  });
}

// A11y (#97): mirror each section header's .is-active class onto the
// disclosure button's aria-expanded. Availability (stay-open) has no
// trigger button, so the null-guard skips it.
function syncAccordionAria(header) {
  const trigger = header?.querySelector('.plp-accordion-trigger');
  if (trigger) trigger.setAttribute('aria-expanded', String(header.classList.contains('is-active')));
}

class PlpFilterDrawer extends HTMLElement {
  constructor() {
    super();
    this.filterDrawer = this.querySelector('[data-filter-drawer]');
    this.filterOverlay = this.querySelector('[data-filter-overlay]');
    this.openFilterBtn = this.querySelector('[data-action="open-filter"]');
    this.closeFilterBtn = this.querySelector('[data-action="close-filter"]');
    this.openSortBtn = this.querySelector('[data-action="open-sort"]');
    this.clearAllBtn = this.querySelector('.plp-filter-drawer__clear-all');
    this.applyBtn = this.querySelector('.plp-filter-drawer__apply');
    this.filterSections = this.querySelectorAll('.plp-filter-drawer__section');
    // Initialize instance properties from class properties
    PlpFilterDrawer.instance = this; // Store instance reference for static access points
    this.filterData = PlpFilterDrawer.filterData || [];
    this.searchParamsInitial = window.location.search.slice(1);
    this.searchParamsPrev = window.location.search.slice(1);

    // 初始化视图
    this.initializeView();
  }

  connectedCallback() {
    this.bindEvents();
    this.initializeSections();
    this.setListeners();
    // Initialize active filters class
    this.updateActiveFiltersClass();
    // Bind filter item events for initial load
    this.bindFilterItemEvents();
    // Restore price bracket radio state from the initial page URL
    this.updateFilterCheckboxes(window.location.search.slice(1));
  }

  setListeners() {
    // Handle browser back/forward navigation
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state?.searchParams || window.location.search.slice(1);
      if (searchParams === this.searchParamsPrev) return;

      // Update the current search params
      this.searchParamsPrev = searchParams;

      // Render the page with the new search params
      this.renderPage(searchParams, null, false);

      // Update filter checkboxes to match the URL parameters
      this.updateFilterCheckboxes(searchParams);
    });
  }

  // Helper method to update filter checkboxes based on URL parameters.
  // An empty string is a valid state (back/forward to the unfiltered entry) and
  // must reset the controls; only bail when no value was passed at all.
  updateFilterCheckboxes(searchParams) {
    if (searchParams == null) return;

    const params = new URLSearchParams(searchParams);

    // Reset all checkboxes first
    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.checked = false;
    });

    // Set checkboxes based on URL parameters
    params.forEach((value, key) => {
      if (key === 'sort_by') return; // Skip sort parameter

      const checkbox = this.querySelector(`input[name="${key}"][value="${decodeURIComponent(value)}"]`);
      if (checkbox) {
        checkbox.checked = true;
      }
    });

    // Sync price bracket radio to match whichever bracket has checked hidden checkboxes.
    this.syncPriceBracketRadio();

    // Resolve any URL-encoded conflicts in exclusive groups (e.g. both
    // "No stone" and "Diamonds" checked — anchor loses).
    this.enforceExclusiveGroups();

    // Update sort selection if present
    const sortValue = params.get('sort_by');
    const sortByElement = document.querySelector('plp-sort-by');
    if (sortValue) {
      const sortRadio = this.querySelector(`input[name="sort_by"][value="${sortValue}"]`);
      if (sortRadio) {
        sortRadio.checked = true;
      }

      if (sortByElement) {
        sortByElement.setAttribute('data-value', sortValue);

        // Update the sort button text
        if (sortRadio && sortRadio.nextElementSibling) {
          const sortButton = sortByElement.querySelector('.plp-sort__text');
          if (sortButton) {
            sortButton.textContent = sortRadio.nextElementSibling.textContent.trim();
          }
        }
      }
    } else if (sortByElement && sortByElement.hasAttribute('data-value')) {
      // Back/forward landed on an entry WITHOUT sort_by: clear the sticky sort
      // state, or the next filter action silently re-appends the old sort via
      // createSearchParams(). Mirrors the data-sort-remove reset path.
      sortByElement.removeAttribute('data-value');
      const sortText = sortByElement.querySelector('.plp-sort__text');
      if (sortText) sortText.textContent = 'Sort By';
      this.querySelectorAll('input[name="sort_by"]').forEach((radio) => {
        radio.checked = false;
      });
    }

    // Update active filters class
    this.updateActiveFiltersClass();
  }

  bindEvents() {
    // Open/Close events for filter
    this.openFilterBtn?.addEventListener('click', () => this.openFilter('filter'));
    this.closeFilterBtn?.addEventListener('click', () => this.closeFilter());

    // Open/Close events for sort (now opens filter drawer)
    this.openSortBtn?.addEventListener('click', () => this.openFilter('sort'));

    this.filterOverlay?.addEventListener('click', (e) => {
      // Only trigger if clicking the overlay itself, not its children
      if (e.target === this.filterOverlay) {
        // Apply filters before closing filter drawer
        this.applyFilters();
      }
    });

    // Filter actions
    this.clearAllBtn?.addEventListener('click', () => this.clearAllFilters());

    this.applyBtn?.addEventListener('click', () => this.applyFilters());

    this.bindPriceBracketToggle();
    this.bindExclusiveGroup();

    // Handle section toggles
    this.filterSections.forEach(section => {
      const header = section.querySelector('h3');
      header?.addEventListener('click', () => this.toggleSection(section));
    });
  }

  initializeSections() {
    this.filterSections.forEach((section) => {
      const content = section.querySelector('.plp-filter-drawer__options, .plp-filter-drawer__option');
      const header = section.querySelector('h3');

      if (!content) return;

      // 为内容添加过渡效果
      content.style.transition = 'max-height 0.3s ease-in-out, padding-top 0.3s ease-in-out, padding-bottom 0.3s ease-in-out';
      content.style.overflow = 'hidden';

      if (section.classList.contains('stay-open')) {
        // 总是保持打开状态的部分
        content.style.display = 'flex';
        content.style.maxHeight = 'none'; // 允许内容自然展开
        if (header) {
          header.classList.add('is-active');
          syncAccordionAria(header);
        }
      } else if (section.classList.contains('is-active') || header?.classList.contains('is-active')) {
        // 具有 is-active 类的部分，应该展开
        content.style.display = 'flex';
        content.style.maxHeight = 'none'; // 允许内容自然展开
        if (header) {
          header.classList.add('is-active');
          syncAccordionAria(header);
        }
      } else {
        // 没有 is-active 类的部分，应该折叠
        content.style.display = 'none';
        content.style.maxHeight = '0';
        if (header) {
          header.classList.remove('is-active');
          syncAccordionAria(header);
        }
      }
    });
  }

  openFilter(triggerType = null) {
    // A11y (#98): remember the invoker for focus restoration on close.
    this.lastDrawerTrigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.filterDrawer.classList.add('is-active');
    this.filterOverlay.classList.add('is-active');
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.classList.add('plp-drawer-open');
    this.trapDrawerFocus();
    // The drawer becomes visible the moment is-active lands (visibility
    // gating flips instantly on open), so it is focusable right away.
    this.closeFilterBtn?.focus({ preventScroll: true });
    
    // Auto-open appropriate accordion based on trigger
    if (triggerType) {
      this.openAppropriateAccordion(triggerType);
    }
  }

  openAppropriateAccordion(triggerType) {
    // Close all sections first (except stay-open ones)
    this.filterSections.forEach(section => {
      if (!section.classList.contains('stay-open')) {
        const header = section.querySelector('h3');
        const content = section.querySelector('.plp-filter-drawer__options, .plp-filter-drawer__option');
        
        if (header && content) {
          header.classList.remove('is-active');
          syncAccordionAria(header);
          content.style.display = 'none';
          content.style.maxHeight = '0';
          content.style.paddingTop = '0';
          content.style.paddingBottom = '0';
        }
      }
    });

    if (triggerType === 'sort') {
      // Open Sort By accordion
      const sortSection = Array.from(this.filterSections).find(section => {
        const header = section.querySelector('h3');
        return header && header.textContent.trim().includes('Sort By');
      });
      
      if (sortSection) {
        this.openSection(sortSection);
      }
    } else if (triggerType === 'filter') {
      // Open first filter accordion (skip Availability and Sort By)
      const filterSections = Array.from(this.filterSections).filter(section => {
        const header = section.querySelector('h3');
        const title = header ? header.textContent.trim() : '';
        return !section.classList.contains('stay-open') && 
               !title.includes('Sort By') && 
               !title.includes('Availability');
      });

      if (filterSections.length > 0) {
        this.openSection(filterSections[0]);
      }
    }
  }

  openSection(section) {
    const header = section.querySelector('h3');
    const content = section.querySelector('.plp-filter-drawer__options, .plp-filter-drawer__option');
    
    if (!header || !content) return;

    header.classList.add('is-active');
    syncAccordionAria(header);

    // Get computed style for padding
    const computedStyle = window.getComputedStyle(content);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);

    // Set initial state
    content.style.display = 'flex';
    content.style.paddingTop = '0';
    content.style.paddingBottom = '0';
    content.style.maxHeight = '0';

    // Trigger reflow
    content.offsetHeight;

    // Animate to open state
    content.style.paddingTop = paddingTop + 'px';
    content.style.paddingBottom = paddingBottom + 'px';
    content.style.maxHeight = content.scrollHeight + paddingTop + paddingBottom + 'px';

    // Clean up after animation. Guarded (#97 review catch): if the user
    // collapsed the section again within the 300ms window, an unconditional
    // maxHeight='none' would cancel the collapse transition mid-flight and
    // strand the panel visibly open while the trigger reports collapsed.
    setTimeout(() => {
      if (header.classList.contains('is-active')) {
        content.style.maxHeight = 'none';
      }
    }, 300);
  }

  closeFilter() {
    const wasOpen = this.filterDrawer.classList.contains('is-active');
    this.filterDrawer.classList.remove('is-active');
    this.filterOverlay.classList.remove('is-active');
    document.documentElement.style.overflow = '';
    document.documentElement.classList.remove('plp-drawer-open');
    this.releaseDrawerFocus();
    if (wasOpen && this.lastDrawerTrigger && document.contains(this.lastDrawerTrigger)) {
      this.lastDrawerTrigger.focus({ preventScroll: true });
    }
    this.lastDrawerTrigger = null;
  }

  // A11y (#98): modal focus containment. Bound on the drawer so it only fires
  // while focus is inside; self-heals if the drawer was closed by another path.
  trapDrawerFocus() {
    if (this.drawerKeydownHandler) return;
    this.drawerKeydownHandler = (e) => {
      if (!this.filterDrawer.classList.contains('is-active')) {
        this.releaseDrawerFocus();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeFilter();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = this.filterDrawer.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const visible = Array.from(focusables).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!visible.length) return;
      const first = visible[0];
      const last = visible[visible.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    this.filterDrawer.addEventListener('keydown', this.drawerKeydownHandler);
  }

  releaseDrawerFocus() {
    if (this.drawerKeydownHandler) {
      this.filterDrawer.removeEventListener('keydown', this.drawerKeydownHandler);
      this.drawerKeydownHandler = null;
    }
  }

  toggleSection(section) {
    const header = section.querySelector('h3');
    const content = section.querySelector('.plp-filter-drawer__options, .plp-filter-drawer__option');

    if (!header || !content) return;

    // 如果动画正在进行中，先移除之前的监听器
    if (content.classList.contains('is-expanding') || content.classList.contains('is-collapsing')) {
      content.style.transition = 'none';
      content.offsetHeight; // 强制重绘
      content.style.transition = 'max-height 0.3s ease-in-out';
      content.classList.remove('is-expanding', 'is-collapsing');
      // 清除可能的未完成的动画监听器
      const clone = content.cloneNode(true);
      content.parentNode.replaceChild(clone, content);
      return this.toggleSection(section); // 重新调用自身
    }

    header.classList.toggle('is-active');
    syncAccordionAria(header);

    if (header.classList.contains('is-active')) {
      // 展开
      // 获取计算样式，考虑padding
      const computedStyle = window.getComputedStyle(content);
      const paddingTop = parseFloat(computedStyle.paddingTop);
      const paddingBottom = parseFloat(computedStyle.paddingBottom);

      // 首先设置为0高度但保持显示
      content.style.display = 'flex';
      content.style.paddingTop = '0';
      content.style.paddingBottom = '0';
      content.style.maxHeight = '0';

      // 触发重绘以使过渡生效
      content.offsetHeight;

      // 设置目标高度，包括padding
      const targetHeight = content.scrollHeight + paddingTop + paddingBottom;
      content.style.maxHeight = targetHeight + 'px';
      content.style.paddingTop = paddingTop + 'px';
      content.style.paddingBottom = paddingBottom + 'px';
      content.classList.add('is-expanding');

      // 动画结束后设置maxHeight为none以允许内容自然调整
      content.addEventListener('transitionend', function expandComplete(e) {
        if (e.propertyName === 'max-height') {
          content.style.maxHeight = 'none';
          content.style.paddingTop = '';
          content.style.paddingBottom = '';
          content.classList.remove('is-expanding');
          content.removeEventListener('transitionend', expandComplete);
        }
      });
    } else {
      // 收起
      // 获取计算样式，考虑padding
      const computedStyle = window.getComputedStyle(content);
      const paddingTop = parseFloat(computedStyle.paddingTop);
      const paddingBottom = parseFloat(computedStyle.paddingBottom);

      // 先设置明确的高度，以便动画可以从此高度开始
      content.style.maxHeight = content.scrollHeight + paddingTop + paddingBottom + 'px';

      // 触发重绘以使过渡生效
      content.offsetHeight;

      // 设置为0高度和0内边距
      content.style.maxHeight = '0';
      content.style.paddingTop = '0';
      content.style.paddingBottom = '0';
      content.classList.add('is-collapsing');

      // 动画结束后隐藏元素
      content.addEventListener('transitionend', function collapseComplete(e) {
        if (e.propertyName === 'max-height') {
          content.style.display = 'none';
          content.style.maxHeight = '';
          content.style.paddingTop = '';
          content.style.paddingBottom = '';
          content.classList.remove('is-collapsing');
          content.removeEventListener('transitionend', collapseComplete);
        }
      });
    }
  }

  clearAllFilters() {
    this.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.checked = false;
    });
    this.querySelectorAll('input[name="price-bracket"]').forEach(radio => {
      radio.checked = false;
    });
    const url = new URL(window.location);
    url.searchParams.delete('sort_by');
    url.searchParams.delete('filter.v.price.gte');
    url.searchParams.delete('filter.v.price.lte');
    window.history.replaceState({}, document.title, url);

    // Reset sort to default (first option)
    const sortByElement = document.querySelector('plp-sort-by');

    sortByElement.removeAttribute('data-value');
    // Update the sort-by element's data-value and text
    sortByElement.querySelector('.plp-sort__text').textContent = 'Sort By';

    // Update active filters class
    this.updateActiveFiltersClass();

    this.onSubmitHandler();
  }

  bindPriceBracketToggle() {
    this.addEventListener('change', (e) => {
      const radio = e.target;
      if (!radio.matches('input[name="price-bracket"]')) return;
      this.querySelectorAll('[data-price-bracket]').forEach(cb => { cb.checked = false; });
      this.querySelectorAll(`[data-price-bracket="${radio.value}"]`).forEach(cb => { cb.checked = true; });
    });
  }

  bindExclusiveGroup() {
    // Mutex behaviour for filter groups marked with data-exclusive-group.
    // Within such a group, the input flagged data-exclusive-anchor (e.g.
    // "No stone") is mutually exclusive with all other inputs. Generic — any
    // future filter wanting an "all-or-nothing" value uses the same markup.
    this.addEventListener('change', (e) => {
      const cb = e.target;
      if (!(cb instanceof HTMLInputElement) || cb.type !== 'checkbox') return;
      if (!cb.checked) return; // only enforce when something becomes checked
      const group = cb.closest('[data-exclusive-group]');
      if (!group) return;
      if (cb.hasAttribute('data-exclusive-anchor')) {
        // Anchor just became checked — uncheck every other input in the group.
        group.querySelectorAll('input[type="checkbox"]:checked').forEach(other => {
          if (other !== cb) other.checked = false;
        });
      } else {
        // A non-anchor became checked — uncheck the anchor if present.
        const anchor = group.querySelector('input[data-exclusive-anchor]:checked');
        if (anchor) anchor.checked = false;
      }
    });
  }

  enforceExclusiveGroups() {
    // Page-load / URL-driven resolution. If both anchor and non-anchor are
    // checked at the same time (hand-crafted URL or stale bookmark), the
    // anchor loses — non-anchor selections were the customer's last specific
    // intent. Their next Apply will produce a clean URL.
    this.querySelectorAll('[data-exclusive-group]').forEach(group => {
      const anchor = group.querySelector('input[data-exclusive-anchor]:checked');
      if (!anchor) return;
      const checkedAll = group.querySelectorAll('input[type="checkbox"]:checked');
      if (checkedAll.length > 1) anchor.checked = false;
    });
  }

  syncPriceBracketRadio() {
    const checked = this.querySelector('[data-price-bracket]:checked');
    const bracket = checked ? checked.dataset.priceBracket : null;
    this.querySelectorAll('input[name="price-bracket"]').forEach(r => {
      r.checked = r.value === bracket;
    });
  }

  applyFilters() {
    // Show loading state
    const productsContainer = document.querySelector("#collection-products");
    if (productsContainer) {
      productsContainer.classList.add("fetching");
    }

    // Update sort option if selected
    const selectedSort = this.querySelector('input[name="sort_by"]:checked');
    const sortByElement = document.querySelector('plp-sort-by');
    if (sortByElement) {
      const sortText = selectedSort ? selectedSort.nextElementSibling.textContent.trim() : 'Sort By';

      sortByElement.querySelector('.plp-sort__text').textContent = sortText;
      if (selectedSort) {
        sortByElement.setAttribute('data-value', selectedSort.value);
      } else {
        sortByElement.removeAttribute('data-value');
      }
    }

    const hasCheckboxFilters = this.querySelectorAll('input[type="checkbox"]:checked').length > 0;

    if (selectedSort || hasCheckboxFilters) {
      this.filterDrawer.querySelector('.plp-filter-drawer__clear-all').style.display = 'flex';
    } else {
      this.filterDrawer.querySelector('.plp-filter-drawer__clear-all').style.display = 'none';
    }

    // Update active filters class
    this.updateActiveFiltersClass();

    // Create search params and render page
    const searchParams = this.createSearchParams();
    this.renderAndUpdateURL(searchParams);
    this.closeFilter();
  }

  renderPage(searchParams, event, updateURLHash = true) {
    this.searchParamsPrev = searchParams;
    const sections = this.getSections();

    // Render generation: each user action supersedes every in-flight response.
    // A slow response (success OR failure) from a superseded action must not
    // touch the grid, and a stale failure must not hand its outdated params to
    // recoverFromFailedRender() — only the latest generation may render.
    this.renderGen = (this.renderGen || 0) + 1;
    const gen = this.renderGen;

    // Show loading state
    document.querySelector("#collection-products")?.classList.add("fetching");

    // Render sections
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const cached = this.filterData.find((entry) => entry.url === url);

      if (cached) {
        this.applySectionHTML(cached.html, event, searchParams);
      } else {
        this.renderSectionFromFetch(url, event, searchParams, gen);
      }
    });

    if (updateURLHash) this.updateURLHash(searchParams);
  }

  renderSectionFromFetch(url, event, searchParams, gen) {
    document.querySelector("#collection-products")?.classList.add("fetching");

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`section fetch ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (gen !== this.renderGen) return; // superseded by a newer action
        // Only cache a response we were actually able to render.
        if (!this.applySectionHTML(html, event, searchParams)) return;
        this.filterData = [
          ...this.filterData,
          { html, url },
        ];
      })
      .catch((error) => {
        console.error('[plp] filter render failed', url, error);
        if (gen !== this.renderGen) return; // superseded by a newer action
        this.recoverFromFailedRender(searchParams);
      });
  }

  // #226: single parse, then validate BEFORE mutating anything. A response with
  // no #collection-products (pre-#226: every /search re-render, because the
  // hardcoded `new-plp` section rendered against a nil collection) previously
  // still ran renderFilters -> the chip row was wiped, and the renderProducts
  // tail -> the summary flipped to "No items" over a stale grid and Load More
  // was hidden. Now nothing on the page is touched.
  applySectionHTML(html, event, searchParams) {
    const doc = new DOMParser().parseFromString(html, "text/html");

    if (!doc.getElementById("collection-products")) {
      document.querySelector("#collection-products")?.classList.remove("fetching");
      console.error('[plp] section response had no #collection-products; skipping render');
      this.recoverFromFailedRender(searchParams);
      return false;
    }

    this.renderFilters(doc, event);
    this.renderProducts(doc);
    document.querySelector("#collection-products")?.classList.remove("fetching");
    return true;
  }

  // Never expected to fire post-#226. If it does, the URL already claims the
  // page is filtered (updateURLHash is synchronous, the fetch is not), so
  // leaving the grid as-is is the one outcome we can't accept — hand off to the
  // server, which renders filters correctly. The one-shot flag makes a reload
  // loop impossible.
  recoverFromFailedRender(searchParams) {
    if (this.renderFallbackFired) return;
    this.renderFallbackFired = true;
    window.location.assign(`${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
  }

  // Takes an already-parsed Document (see applySectionHTML), which both
  // guarantees #collection-products is present and saves a second DOM parse.
  renderProducts(parsedHTML) {
    const productsContainer = document.querySelector("#collection-products");

    if (productsContainer) {
      productsContainer.innerHTML = parsedHTML.getElementById("collection-products").innerHTML;
      productsContainer.classList.remove("fetching");

      // #189: load-more appends extra .plp-grid-wrapper pages (id stripped, see
      // new-plp.js loadMoreProducts) into the grid container. A filter render only
      // swaps the innerHTML of the single on-page #collection-products wrapper, so
      // any appended pages would otherwise remain below showing stale, unfiltered
      // products. Remove every OTHER wrapper so a filter render always yields
      // exactly one grid wrapper. Summaries (.plp-grid-item-pagination-text) and the
      // pagination / load-previous containers live elsewhere and are untouched.
      const gridContainer = productsContainer.closest(".plp-grid-container");
      if (gridContainer) {
        gridContainer.querySelectorAll(".plp-grid-wrapper").forEach((wrapper) => {
          if (wrapper !== productsContainer) {
            wrapper.remove();
          }
        });

        // #189 (review): bump the render generation so any load-more /
        // load-previous fetch still in flight (new-plp.js) can tell the grid
        // it targeted has been swapped, and discards its stale, unfiltered
        // response instead of injecting it into the freshly filtered grid.
        gridContainer.dataset.plpRenderGen = String(parseInt(gridContainer.dataset.plpRenderGen || "0", 10) + 1);
      }
    }

    setTimeout(() => {
      const gridItemCount = parsedHTML.querySelectorAll('.plp-grid-item:not(.plp-grid-banner)').length;
      if (gridItemCount > 0) {
        if (!document.querySelector('.plp-grid-container .plp-grid-item-pagination-text [data-summary-count]')) {
          document.querySelector('.plp-grid-container .plp-grid-item-pagination-text').innerHTML = parsedHTML.querySelector('.plp-grid-container .plp-grid-item-pagination-text').innerHTML;
        }

        document.querySelectorAll('[data-summary-count]').forEach(item => {
          item.innerHTML = gridItemCount;
        });
      } else {
        document.querySelectorAll('.plp-grid-container .plp-grid-item-pagination-text').forEach(item => {
          item.innerHTML = "No items";
        });
      }

      if (parsedHTML.querySelector('[data-summary-total]')) {
        document.querySelector('[data-summary-total]').innerHTML = parsedHTML.querySelector('[data-summary-total]').innerHTML;
      }

      // A11y (#102): announce the filtered result state.
      const liveRegion = document.querySelector('[data-plp-live-region]');
      if (liveRegion) {
        const total = document.querySelector('[data-summary-total]');
        liveRegion.textContent = gridItemCount > 0
          ? `You have viewed ${gridItemCount} of ${total ? total.textContent.trim() : gridItemCount} items`
          : 'No items';
      }

      // #189: markup uses the CLASS .plp-load-previous-btn (sections/new-plp.liquid,
      // templates/search.liquid), not a <plp-load-previous-btn> custom element. The
      // old bare-tag selector matched nothing, so the Load Previous button's
      // visibility was never synced on filter apply.
      const previousBtn = document.querySelector('.plp-load-previous-btn');
      const parsedPreviousBtn = parsedHTML.querySelector('.plp-load-previous-btn');
      if (previousBtn && parsedPreviousBtn) {
        previousBtn.style.display = parsedPreviousBtn.style.display;

        // #189 (review): when the rendered URL points at page > 1 (e.g. the
        // popstate re-render path), the parsed fragment's button carries the
        // correct data-previous-url for that paginate state, while the live
        // button still holds its initial-load value (blank when the session
        // started on page 1) -- clicking it would fetch the wrong page. Sync
        // it like #110 does for data-next. data-previous-url is the only
        // data-* attribute the load-previous flow reads (new-plp.js
        // loadPreviousProducts).
        const previousBtnButton = previousBtn.querySelector('button');
        const parsedPreviousBtnButton = parsedPreviousBtn.querySelector('button');
        if (previousBtnButton && parsedPreviousBtnButton) {
          const previousUrl = parsedPreviousBtnButton.getAttribute('data-previous-url');
          if (previousUrl) {
            previousBtnButton.setAttribute('data-previous-url', previousUrl);
          } else {
            previousBtnButton.removeAttribute('data-previous-url');
          }
        }
      }
    }, 20);

    // const filterDrawer = document.querySelector("plp-filter-drawer .plp-filter-drawer");
    // if (filterDrawer && parsedHTML.querySelector("plp-filter-drawer .plp-filter-drawer")) {
    //   filterDrawer.innerHTML = parsedHTML.querySelector("plp-filter-drawer").innerHTML;
    // }

    // const drawerPanel = document.querySelector("[data-drawer-panel]");
    // if (drawerPanel && parsedHTML.querySelector("[data-drawer-panel]")) {
    //   drawerPanel.innerHTML = parsedHTML.querySelector("[data-drawer-panel]").innerHTML;
    // }

    // #110: the templates render (and loadMoreProducts reads) data-next —
    // the old data-next-url attribute existed nowhere in the rendered HTML,
    // so the live button kept its pre-filter URL forever.
    const moreBtn = document.querySelector("[data-more-btn]");
    const parsedMoreBtn = parsedHTML.querySelector("[data-more-btn]");
    if (moreBtn && parsedMoreBtn) {
      const nextUrl = parsedMoreBtn.getAttribute("data-next");
      if (nextUrl) {
        moreBtn.setAttribute("data-next", nextUrl);
      } else {
        moreBtn.removeAttribute("data-next");
      }
      moreBtn.style.display = parsedMoreBtn.style.display;
    } else if (moreBtn) {
      moreBtn.style.display = "none";
    }
  }

  // Takes an already-parsed Document (see applySectionHTML) — the response has
  // been validated, so this can no longer wipe the chip row off a page whose
  // re-render failed.
  renderFilters(parsedHTML) {
    if (parsedHTML.querySelector(".plp-filter-selected")) {
      if (document.querySelector(".plp-filter-selected")) {
        document.querySelector(".plp-filter-selected").innerHTML = parsedHTML.querySelector(".plp-filter-selected").innerHTML;
      } else {
        document.querySelector(".plp-filter-sort-by-container").appendChild(parsedHTML.querySelector(".plp-filter-selected"));
      }

      // Re-bind event listeners for newly created sort-item and filter-item elements
      this.bindFilterItemEvents();
    } else {
      document.querySelector(".plp-filter-selected")?.remove();
    }

    // Update active filters class after rendering
    this.updateActiveFiltersClass();
  }

  bindFilterItemEvents() {
    // Bind events for sort-item elements
    document.querySelectorAll('sort-item[data-sort-remove]').forEach(sortItem => {
      const removeBtn = sortItem.querySelector('[data-sort-remove]');
      if (removeBtn && !removeBtn.hasAttribute('data-event-bound')) {
        removeBtn.setAttribute('data-event-bound', 'true');
        removeBtn.addEventListener('click', () => {
          const defaultSortValue = sortItem.getAttribute('data-sort-default');

          // Remove sort_by from URL
          const url = new URL(window.location);
          url.searchParams.delete('sort_by');
          window.history.replaceState({}, document.title, url);

          const sortByElement = document.querySelector('plp-sort-by');

          if (sortByElement) {
            // Remove data-value attribute instead of setting default
            sortByElement.removeAttribute('data-value');

            // Update the sort text to default
            sortByElement.querySelector('.plp-sort__text').textContent = 'Sort By';

            // Uncheck all sort options
            this.querySelectorAll('input[name="sort_by"]').forEach(radio => {
              radio.checked = false;
            });
          }

          // Remove the sort-item element
          const filterSelectedContainer = document.querySelector('.plp-filter-selected');

          // Check if there are other filter items besides this sort-item
          const otherFilterItems = filterSelectedContainer.querySelectorAll('.plp-filter-selected__item:not(sort-item)');

          if (otherFilterItems.length > 0) {
            // Remove only this sort-item
            if (sortItem.parentElement && sortItem.parentElement.contains(sortItem)) {
              sortItem.parentElement.removeChild(sortItem);
            }
          } else {
            // Remove the entire filter selected container if this is the only item
            const filterSortByContainer = document.querySelector('.plp-filter-sort-by-container');
            if (filterSelectedContainer && filterSortByContainer && filterSortByContainer.contains(filterSelectedContainer)) {
              filterSortByContainer.removeChild(filterSelectedContainer);
            }
          }

          // Update active filters class
          this.updateActiveFiltersClass();

          // Create search params and render page without adding sort_by back
          const searchParams = this.createSearchParams();
          this.renderAndUpdateURL(searchParams);
        });
      }
    });

    // Bind events for filter-item elements (if not already bound)
    document.querySelectorAll('filter-item[data-filter-remove]').forEach(filterItem => {
      const removeBtn = filterItem.querySelector('[data-filter-remove]');
      if (removeBtn && !removeBtn.hasAttribute('data-event-bound')) {
        removeBtn.setAttribute('data-event-bound', 'true');
        // Filter item events are already handled by the filter-item custom element
      }
    });
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  getSections() {
    // #226: server-declared section id (snippets/plp-filter-sort-by.liquid) —
    // "new-plp" on collections, "new-plp-search" on /search. The hardcoded
    // "new-plp" used to be fetched on search too, where it renders against a nil
    // collection and returns a grid-less shell, so every filter/sort/chip action
    // silently no-opped. The literal fallback preserves pre-#226 behaviour if a
    // stale cached copy of this asset meets fresh Liquid mid-deploy.
    return [
      {
        section: this.dataset.plpSectionId || "new-plp",
      },
    ];
  }

  createSearchParams() {

    let base = '';

    // Only add sort_by if data-value is not empty and not default
    const sortByElement = document.querySelector("plp-sort-by");
    const sortByValue = sortByElement ? sortByElement.getAttribute("data-value") : '';
    const firstSortOption = this.querySelector('input[name="sort_by"]');
    // const defaultSortValue = firstSortOption ? firstSortOption.value : '';

    if (sortByValue && sortByValue.trim() !== '') {
      base = `sort_by=${sortByValue}`;
    }

    // #226: carry the search-scoping params through the re-render (was `q` only).
    // Without `q` the fetched section has no results at all; without `type` Shopify
    // returns a mixed-object search whose `search.filters` set and `paginate.items`
    // total both differ from the page the user is looking at. `type` is PRESERVED,
    // never injected — synthesising it would silently change the visible result set
    // for anyone who arrived on an unscoped /search URL.
    const urlParams = new URLSearchParams(window.location.search);
    PRESERVED_SEARCH_PARAMS.forEach((key) => {
      const value = urlParams.get(key);
      if (value === null || value === '') return;
      const pair = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      base += base ? `&${pair}` : pair;
    });

    this.querySelectorAll("input[type='checkbox']:checked").forEach((item) => {
      base += base ? `&${item.getAttribute("name")}=${encodeURIComponent(item.getAttribute("value"))}` : `${item.getAttribute("name")}=${encodeURIComponent(item.getAttribute("value"))}`;
    });

    return base;
  }

  renderAndUpdateURL(searchParams) {
    this.renderPage(searchParams, null, true);
  }

  updateSelectedFiltersDisplay() {
    const selectedContainer = document.querySelector('.plp-filter-selected');
    if (!selectedContainer) return;

    // Clear existing selected filters
    selectedContainer.innerHTML = '';

    // Add new selected filters
    this.selectedFilters.forEach((values, type) => {
      values.forEach(value => {
        const filterItem = document.createElement('div');
        filterItem.className = 'plp-filter-selected__item carousel-cell';
        filterItem.innerHTML = `
          <span>${this.formatFilterLabel(value)}</span>
          <span class="remove-filter" data-type="${type}" data-value="${value}">
            {% render 'icon-close' %}
          </span>
        `;
        selectedContainer.appendChild(filterItem);
      });
    });

    // Add clear all button if there are filters
    if (this.selectedFilters.size > 0) {
      const clearAll = document.createElement('div');
      clearAll.className = 'plp-filter-selected__clear';
      clearAll.textContent = 'Clear All';
      clearAll.addEventListener('click', () => this.clearAllFilters());
      selectedContainer.appendChild(clearAll);
    }
  }

  formatFilterLabel(value) {
    return value.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }

  onSubmitForm(searchParams, event) {
    this.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    // event.preventDefault();
    document.querySelector("#collection-products").classList.add("fetching");
    const searchParams = this.createSearchParams(event);
    this.onSubmitForm(searchParams, event);
    // Update active filters class
    this.updateActiveFiltersClass();
  }

  initializeView() {
    // 尝试从localStorage读取之前的视图偏好
    let savedView;
    try {
      // 根据当前设备类型选择合适的存储键
      const storageKey = this.isMobile ? 'adorn-mobile-view' : 'adorn-desktop-view';

      // 首先尝试使用设备特定的键
      savedView = localStorage.getItem(storageKey);

      // 如果没有找到，再尝试使用通用键
      if (!savedView) {
        savedView = localStorage.getItem('adorn-collection-view');
      }
    } catch (e) {
      console.warn('Failed to read view preference from localStorage');
    }

    // 如果有保存的视图偏好，应用它
    if (savedView) {
      this.activeView = savedView;

      // 找到对应的视图按钮并设置为激活状态
      const viewType = this.isMobile ? '.mobile-view-switch' : '.desktop-view-switch';
      const viewButton = this.querySelector(`${viewType} .view-btn[data-view="${savedView}"]`);

      if (viewButton) {
        // 移除同组中其他按钮的激活状态
        const viewGroup = viewButton.closest('.plp-view-switch');
        viewGroup.querySelectorAll('.view-btn').forEach(btn => {
          btn.classList.remove('active');
        });

        // 设置此按钮为激活状态
        viewButton.classList.add('active');
      }
      syncViewBtnPressed();
    } else {
      // 没有保存的偏好时，使用当前活动按钮
      const activeButton = this.querySelector('.view-btn.active');
      if (activeButton) {
        this.activeView = activeButton.getAttribute('data-view');
      }
    }

    // 根据设备类型记录初始视图
    if (this.isMobile) {
      this.lastMobileView = this.activeView;
    } else {
      this.lastDesktopView = this.activeView;
    }
  }

  updateActiveFiltersClass() {
    const filterContainer = document.querySelector('.plp-filter-sort-by-container');
    if (!filterContainer) return;

    // Check for active checkbox filters
    const hasActiveCheckboxFilters = this.querySelectorAll('input[type="checkbox"]:checked').length > 0;

    // Check for non-default sort option
    const sortByElement = document.querySelector('plp-sort-by');
    const currentSortValue = sortByElement ? sortByElement.getAttribute('data-value') : '';

    // Toggle class based on active filters or sort
    if (hasActiveCheckboxFilters || currentSortValue) {
      filterContainer.classList.add('has-active-filters');
    } else {
      filterContainer.classList.remove('has-active-filters');
    }
  }


}

// Initialize static properties
PlpFilterDrawer.filterData = [];
PlpFilterDrawer.searchParamsInitial = window.location.search.slice(1);
PlpFilterDrawer.searchParamsPrev = window.location.search.slice(1);

if (!customElements.get('plp-filter-drawer')) {
  customElements.define('plp-filter-drawer', PlpFilterDrawer);
}

if (!customElements.get('plp-sort-by')) {
  customElements.define('plp-sort-by', class extends HTMLElement {
    constructor() {
      super();
      this.sortByContainer = this.closest('.plp-sort-by-container');
      this.sortByText = this.querySelector('.plp-sort__text');
      this.sortValue = this.getAttribute('data-value');
    }

    connectedCallback() {
      // Only keep the history change handling
      window.addEventListener('popstate', this.handleHistoryChange.bind(this));
    }

    handleHistoryChange(event) {
      const searchParams = event.state?.searchParams || window.location.search.slice(1);
      if (!searchParams) return;

      const params = new URLSearchParams(searchParams);
      const sortValue = params.get('sort_by');

      if (sortValue) {
        this.setAttribute('data-value', sortValue);

        // Update the sort text by finding the matching radio in the drawer
        const sortDrawer = document.querySelector('[data-sort-drawer]');
        if (sortDrawer) {
          const option = sortDrawer.querySelector(`input[value="${sortValue}"]`);
          if (option && option.nextElementSibling) {
            this.sortByText.textContent = option.nextElementSibling.textContent.trim();
          }
        }
      } else {
        this.sortByText.textContent = 'Sort By';
      }
    }
  });
}

if (!customElements.get('filter-item')) {
  customElements.define('filter-item', class extends HTMLElement {
    constructor() {
      super();
      console.log('filter-item', this);
    }

    connectedCallback() {
      console.log(this.querySelector('[data-filter-remove]'));
      this.querySelector('[data-filter-remove]').addEventListener('click', () => {
        const filterName = this.querySelector('[data-filter-remove]').getAttribute('data-filter-remove');
        const filterValue = this.querySelector('[data-filter-remove]').getAttribute('data-filter-value');

        document.querySelectorAll(`input[name="${filterName}"]`).forEach(item => {
          if (item.getAttribute('value') === filterValue) {
            item.click();
            const filterDrawer = document.querySelector('plp-filter-drawer');
            if (filterDrawer) {
              filterDrawer.applyFilters();
            }

            // Check if there are multiple filter items
            if (this.parentElement && this.parentElement.querySelectorAll('.plp-filter-selected__item').length > 1) {
              // Remove only this item
              if (this.parentElement.contains(this)) {
                this.parentElement.removeChild(this);
              }
            } else {
              // Remove the entire filter selected container
              const filterSelectedContainer = document.querySelector('.plp-filter-selected');
              const filterSortByContainer = document.querySelector('.plp-filter-sort-by-container');

              if (filterSelectedContainer && filterSortByContainer && filterSortByContainer.contains(filterSelectedContainer)) {
                filterSortByContainer.removeChild(filterSelectedContainer);
              }
            }
          }
        });

        // A11y (#96): the activated button is destroyed with its chip (and the
        // row is re-rendered async) - move focus to the stable FILTERS trigger
        // so keyboard users aren't dropped to <body>.
        document.querySelector('[data-action="open-filter"]')?.focus({ preventScroll: true });
      });
    }
  });
}


if (!customElements.get('price-bracket-item')) {
  customElements.define('price-bracket-item', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-price-bracket-remove]').addEventListener('click', () => {
        const filterDrawer = document.querySelector('plp-filter-drawer');
        if (!filterDrawer) return;
        filterDrawer.querySelectorAll('input[name="price-bracket"]').forEach(r => { r.checked = false; });
        filterDrawer.querySelectorAll('[data-price-bracket]').forEach(cb => { cb.checked = false; });
        filterDrawer.applyFilters();
        const siblings = this.parentElement?.querySelectorAll('.plp-filter-selected__item');
        if (siblings && siblings.length > 1) {
          this.remove();
        } else {
          document.querySelector('.plp-filter-selected')?.remove();
        }

        // A11y (#96): the activated button is destroyed with its chip (and the
        // row is re-rendered async) - move focus to the stable FILTERS trigger
        // so keyboard users aren't dropped to <body>.
        document.querySelector('[data-action="open-filter"]')?.focus({ preventScroll: true });
      });
    }
  });
}

if (!customElements.get('clear-all-filters')) {
  customElements.define('clear-all-filters', class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }

    clearAllFilters() {
      const filterDrawer = document.querySelector('plp-filter-drawer');
      const sortByElement = document.querySelector('plp-sort-by');
      filterDrawer.clearAllFilters();

      if (filterDrawer) {
        // Remove sort_by from URL
        const url = new URL(window.location);
        url.searchParams.delete('sort_by');
        window.history.replaceState({}, document.title, url);

        filterDrawer.querySelectorAll('input[name="sort_by"]').forEach(radio => {
          radio.checked = false
        });

        document.querySelector('plp-sort-by').removeAttribute('data-value');

        // Also reset sort to default
        if (sortByElement) {
          // Update the sort text to default
          sortByElement.querySelector('.plp-sort__text').textContent = 'Sort By';
        }

        filterDrawer.applyFilters();
      }

      // Safely remove the filter selected container if it exists
      const filterSelectedContainer = document.querySelector('.plp-filter-selected');
      const filterSortByContainer = document.querySelector('.plp-filter-sort-by-container');

      if (filterSelectedContainer && filterSortByContainer && filterSortByContainer.contains(filterSelectedContainer)) {
        filterSortByContainer.removeChild(filterSelectedContainer);
      }

      // A11y (#96): same focus restoration as the chip-remove buttons.
      document.querySelector('[data-action="open-filter"]')?.focus({ preventScroll: true });
    }
  });
}

if (!customElements.get('column-toggle')) {
  customElements.define('column-toggle', class extends HTMLElement {
    constructor() {
      super();
      this.activeView = 'list'; // 默认视图

      // 设备类型检测
      this.isMobile = window.innerWidth <= 768;

      // 记录上次选择的视图（分别为移动端和桌面端）
      this.lastDesktopView = localStorage.getItem('adorn-desktop-view') || 'list';
      this.lastMobileView = localStorage.getItem('adorn-mobile-view') || 'two-column';
    }

    connectedCallback() {
      // DOM-dependent work runs here so children are guaranteed parsed.
      this.viewButtons = this.querySelectorAll('.view-btn');

      // Attach listeners first — initializeView/applyView can throw if
      // sibling custom elements (plp-grid-item) haven't upgraded yet, and
      // an uncaught throw before this point would leave buttons unbound.
      this.viewButtons.forEach(button => {
        button.addEventListener('click', this.handleViewChange.bind(this));
      });
      window.addEventListener('resize', this.handleResize.bind(this));

      this.initializeView();
      this.applyView();
    }

    initializeView() {
      // 尝试从localStorage读取之前的视图偏好
      let savedView;
      try {
        // 根据当前设备类型选择合适的存储键
        const storageKey = this.isMobile ? 'adorn-mobile-view' : 'adorn-desktop-view';

        // 首先尝试使用设备特定的键
        savedView = localStorage.getItem(storageKey);

        // 如果没有找到，再尝试使用通用键
        if (!savedView) {
          savedView = localStorage.getItem('adorn-collection-view');
        }
      } catch (e) {
        console.warn('Failed to read view preference from localStorage');
      }

      // 如果有保存的视图偏好，应用它
      if (savedView) {
        this.activeView = savedView;

        // 找到对应的视图按钮并设置为激活状态
        const viewType = this.isMobile ? '.mobile-view-switch' : '.desktop-view-switch';
        const viewButton = this.querySelector(`${viewType} .view-btn[data-view="${savedView}"]`);

        if (viewButton) {
          // 移除同组中其他按钮的激活状态
          const viewGroup = viewButton.closest('.plp-view-switch');
          viewGroup.querySelectorAll('.view-btn').forEach(btn => {
            btn.classList.remove('active');
          });

          // 设置此按钮为激活状态
          viewButton.classList.add('active');
        }
        syncViewBtnPressed();
      } else {
        // 没有保存的偏好时，使用当前活动按钮
        const activeButton = this.querySelector('.view-btn.active');
        if (activeButton) {
          this.activeView = activeButton.getAttribute('data-view');
        }
      }

      // 根据设备类型记录初始视图
      if (this.isMobile) {
        this.lastMobileView = this.activeView;
      } else {
        this.lastDesktopView = this.activeView;
      }
    }

    handleViewChange(event) {
      const button = event.currentTarget;
      const viewType = button.getAttribute('data-view');

      // 更新当前视图
      this.activeView = viewType;

      // 记录当前设备类型的视图选择
      if (this.isMobile) {
        this.lastMobileView = viewType;
        localStorage.setItem('adorn-mobile-view', viewType);
      } else {
        this.lastDesktopView = viewType;
        localStorage.setItem('adorn-desktop-view', viewType);
      }

      // 更新按钮状态
      this.updateButtonStates(button);

      // 应用视图样式
      this.applyView();

      document.querySelectorAll('plp-grid-item').forEach(item => {
        // resize() is defined on the upgraded plp-grid-item custom element.
        // Skip when the element hasn't upgraded (e.g. new-plp.js not loaded yet).
        if (typeof item.resize === 'function') item.resize();
      });
    }

    updateButtonStates(activeButton) {
      // 获取当前视图组（移动端或桌面端）
      const viewGroup = activeButton.closest('.plp-view-switch');

      // 移除同组内所有按钮的active状态
      viewGroup.querySelectorAll('.view-btn').forEach(btn => {
        btn.classList.remove('active');
      });

      // 添加当前按钮的active状态
      activeButton.classList.add('active');
      syncViewBtnPressed();
    }

    handleResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;

      // 如果设备类型发生变化（从移动到桌面或反之）
      if (wasMobile !== this.isMobile) {
        // 根据当前设备类型切换到对应的上次选择的视图
        this.activeView = this.isMobile ? this.lastMobileView : this.lastDesktopView;

        // 更新按钮状态
        const viewType = this.isMobile ? '.mobile-view-switch' : '.desktop-view-switch';
        const activeButton = this.querySelector(`${viewType} .view-btn[data-view="${this.activeView}"]`);
        if (activeButton) {
          this.updateButtonStates(activeButton);
        }

        // 应用视图样式
        this.applyView();
      }
    }

    applyView() {
      // 每次调用时重新获取所有 collection-products 元素
      // #189: load-more appends extra wrappers with the id stripped (duplicate-id
      // fix in new-plp.js), so an id-only query would restyle just the first
      // wrapper. Include class-scoped wrappers; the union dedupes automatically.
      const collectionProducts = document.querySelectorAll('#collection-products, .plp-grid-container .plp-grid-wrapper');

      if (!collectionProducts || collectionProducts.length === 0) {
        document.querySelector('.plp-wrapper').classList.remove('plp-hidden');
        return;
      }

      // 对所有 collection-products 元素应用视图样式
      collectionProducts.forEach(collectionProduct => {
        // 移除所有可能的视图类
        collectionProduct.classList.remove('view-list', 'view-four-column', 'view-three-column', 'view-two-column', 'view-one-column');

        // 添加当前视图类
        collectionProduct.classList.add(`view-${this.activeView}`);
        console.log(collectionProduct);
      });

      // 存储当前视图设置到 localStorage，以便页面刷新后保持
      try {
        // 保存到通用键
        localStorage.setItem('adorn-collection-view', this.activeView);

        // 同时也保存到设备特定键
        if (this.isMobile) {
          localStorage.setItem('adorn-mobile-view', this.activeView);
        } else {
          localStorage.setItem('adorn-desktop-view', this.activeView);
        }
      } catch (e) {
        console.warn('Failed to save view preference to localStorage');
      }

      document.querySelector('.plp-wrapper').classList.remove('plp-hidden');

      document.querySelectorAll('plp-grid-item').forEach(item => {
        // resize() is defined on the upgraded plp-grid-item custom element.
        // Skip when the element hasn't upgraded (e.g. new-plp.js not loaded yet).
        if (typeof item.resize === 'function') item.resize();
      });
    }
  });
}

if (!customElements.get('plp-spotlight')) {
  customElements.define('plp-spotlight', class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
    }
  });
}

if (!customElements.get('sort-item')) {
  customElements.define('sort-item', class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.querySelector('[data-sort-remove]').addEventListener('click', () => {
        // const currentSortValue = this.getAttribute('data-sort-value');
        const defaultSortValue = this.getAttribute('data-sort-default');

        // Remove sort_by from URL
        const url = new URL(window.location);
        url.searchParams.delete('sort_by');
        window.history.replaceState({}, document.title, url);

        // Reset to default sort
        const filterDrawer = document.querySelector('plp-filter-drawer');
        const sortByElement = document.querySelector('plp-sort-by');

        if (sortByElement) {
          // Remove data-value attribute instead of setting default
          sortByElement.removeAttribute('data-value');

          // Update the sort text to default
          sortByElement.querySelector('.plp-sort__text').textContent = 'Sort By';

          // Uncheck all sort options
          filterDrawer.querySelectorAll('input[name="sort_by"]').forEach(radio => {
            radio.checked = false;
          });
        }

        // Remove the sort-item element
        const filterSelectedContainer = document.querySelector('.plp-filter-selected');

        // Check if there are other filter items besides this sort-item
        const otherFilterItems = filterSelectedContainer.querySelectorAll('.plp-filter-selected__item:not(sort-item)');

        if (otherFilterItems.length > 0) {
          // Remove only this sort-item
          if (this.parentElement && this.parentElement.contains(this)) {
            this.parentElement.removeChild(this);
          }
        } else {
          // Remove the entire filter selected container if this is the only item
          const filterSortByContainer = document.querySelector('.plp-filter-sort-by-container');
          if (filterSelectedContainer && filterSortByContainer && filterSortByContainer.contains(filterSelectedContainer)) {
            filterSortByContainer.removeChild(filterSelectedContainer);
          }
        }

        if (document.querySelector('plp-sort-by')) {
          document.querySelector('plp-sort-by').removeAttribute('data-value')
        }

        // Update active filters class
        if (filterDrawer) {
          filterDrawer.updateActiveFiltersClass();

          // Create search params and render page without adding sort_by back
          const searchParams = filterDrawer.createSearchParams();
          filterDrawer.renderAndUpdateURL(searchParams);
        }

        // A11y (#96): the activated button is destroyed with its chip (and the
        // row is re-rendered async) - move focus to the stable FILTERS trigger
        // so keyboard users aren't dropped to <body>.
        document.querySelector('[data-action="open-filter"]')?.focus({ preventScroll: true });
      });
    }
  });
}